import {
    CheckCircleFilled,
    CloseCircleFilled,
    DollarOutlined,
    DownloadOutlined,
    EyeOutlined,
    FileExcelOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert, Button, Card, Col, Progress, Row, Space, Statistic, Steps, Table, Tag, Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import * as XLSX from "xlsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;
const POLL_INTERVAL_MS = 2500;
const ETAT_LABELS = { EN_COURS: "En cours", TERMINE: "Terminé", ERREUR: "Erreur", INCONNU: "Inconnu" };
const fmt = (n) => (n == null ? "0" : Math.round(n).toLocaleString("fr-FR")) + " FCFA";

export default function RepriseGumVtHonoraires() {
    const [step, setStep]         = useState(0);   // 0 sélection, 1 aperçu, 2 application, 3 rapport
    const [fichier, setFichier]   = useState(null);
    const [apercu, setApercu]     = useState(null);
    const [rapport, setRapport]   = useState(null);
    const [loading, setLoading]   = useState(false);
    const [progress, setProgress] = useState(0);
    const [jobId, setJobId]       = useState(null);
    const [jobEtat, setJobEtat]   = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const previsualiser = async () => {
        if (!fichier) { toast.warning("Sélectionnez le fichier Excel."); return; }
        setLoading(true); setApercu(null);
        try {
            const form = new FormData(); form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/corriger-honoraires/preview", form,
                { headers: { "Content-Type": "multipart/form-data" } });
            setApercu(res.data); setStep(1);
        } catch (err) {
            toast.error("Erreur aperçu : " + (err.response?.data?.message || err.message));
        } finally { setLoading(false); }
    };

    const appliquer = async () => {
        if (!fichier) return;
        setLoading(true); setRapport(null); setProgress(5); setJobEtat("EN_COURS"); setStep(2);
        try {
            const form = new FormData(); form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/corriger-honoraires", form,
                { headers: { "Content-Type": "multipart/form-data" } });
            const id = res.data?.jobId;
            if (!id) throw new Error("Aucun jobId reçu du serveur.");
            setJobId(id); demarrerPolling(id);
        } catch (err) {
            setLoading(false); setJobEtat("ERREUR");
            toast.error("Erreur : " + (err.response?.data?.message || err.message));
        }
    };

    const demarrerPolling = useCallback((id) => {
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks++;
            setProgress(p => Math.min(p + Math.max(1, 5 - Math.floor(ticks / 5)), 90));
            try {
                const res = await axiosInstance.get(`/api/migration/job/${id}`);
                const statut = res.data;
                if (statut.etat === "TERMINE" || statut.etat === "ERREUR") {
                    clearInterval(pollRef.current);
                    setProgress(100); setJobEtat(statut.etat); setLoading(false);
                    if (statut.etat === "TERMINE") {
                        setRapport(statut.resultat); setStep(3);
                        toast.success("Correction des honoraires terminée !");
                    } else {
                        toast.error("Erreur : " + (statut.erreur || "Erreur inconnue"));
                    }
                }
            } catch (err) {
                clearInterval(pollRef.current); setLoading(false); setJobEtat("ERREUR");
                toast.error("Erreur polling : " + err.message);
            }
        }, POLL_INTERVAL_MS);
    }, []);

    const exportRapport = (d) => {
        if (!d) return;
        const wb = XLSX.utils.book_new();
        const resume = [
            ["Indicateur", "Valeur"],
            ["Lignes honoraires (fichier)", d.totalLignesHonoraires],
            ["Décaissements corrigés", d.corriges],
            ["Déjà corrigés", d.dejaCorriges],
            ["Sinistres impactés", d.sinistresImpactes],
            ["Décaissements introuvables", d.nonTrouves],
            ["Montants incohérents (non touchés)", d.montantIncoherent],
            ["Sinistres introuvables", d.sinistresIntrouvables],
            ["Total honoraires retirés des montants", Math.round(d.montantRetireTotal)],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resume), "Résumé");
        const ech = [
            ["N° sinistre", "N° règlement", "Montant avant", "Montant après", "Honoraire (taxe)"],
            ...(d.echantillon || []).map(e => [e.numSinistre, e.numeroReglement,
                Math.round(e.montantAvant), Math.round(e.montantApres), Math.round(e.taxe)]),
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ech), "Échantillon");
        const err = [["Détail"], ...(d.lignesErreur || []).map(x => [x])];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(err), "Anomalies");
        XLSX.writeFile(wb, `correction_honoraires_gumvt_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const reinitialiser = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep(0); setFichier(null); setApercu(null); setRapport(null);
        setLoading(false); setProgress(0); setJobId(null); setJobEtat(null);
    };

    const colsEch = [
        { title: "N° sinistre", dataIndex: "numSinistre", key: "numSinistre", width: 150,
          render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
        { title: "N° règl.", dataIndex: "numeroReglement", key: "numeroReglement", width: 80, align: "center" },
        { title: "Montant avant", dataIndex: "montantAvant", key: "montantAvant", align: "right",
          render: v => <Text type="danger">{fmt(v)}</Text> },
        { title: "Montant après", dataIndex: "montantApres", key: "montantApres", align: "right",
          render: v => <Text type="success">{fmt(v)}</Text> },
        { title: "Honoraire (taxe)", dataIndex: "taxe", key: "taxe", align: "right",
          render: v => <Tag color="blue">{fmt(v)}</Tag> },
    ];

    const Indics = ({ d }) => (
        <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Décaissements corrigés" value={d.corriges}
                    valueStyle={{ color: "#52c41a", fontSize: 18 }} prefix={<CheckCircleFilled />} />
            </Col>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Sinistres impactés" value={d.sinistresImpactes}
                    valueStyle={{ fontSize: 18, color: "#242166" }} />
            </Col>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Honoraires retirés" value={Math.round(d.montantRetireTotal)} suffix="F"
                    valueStyle={{ fontSize: 16, color: "#242166" }} />
            </Col>
            <Col xs={12} sm={8} md={5}>
                <Statistic title="Déjà corrigés" value={d.dejaCorriges}
                    valueStyle={{ fontSize: 18, color: "#8c8c8c" }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
                <Statistic title="Introuvables" value={(d.nonTrouves || 0) + (d.sinistresIntrouvables || 0)}
                    valueStyle={{ color: (d.nonTrouves + d.sinistresIntrouvables) > 0 ? "#faad14" : "#52c41a", fontSize: 18 }} />
            </Col>
        </Row>
    );

    return (
        <PageLayout currentMenu="REPRISE">
            <div style={{ padding: "24px 0" }}>
                <Space align="center" style={{ marginBottom: 24 }}>
                    <DollarOutlined style={{ fontSize: 28, color: "#242166" }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#242166" }}>
                            Corriger les honoraires — Pool GUM / VT
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Retire la taxe/honoraire qui avait été additionnée au montant du règlement
                        </Text>
                    </div>
                </Space>

                <Steps current={step} style={{ marginBottom: 32 }} items={[
                    { title: "Sélection" }, { title: "Aperçu" }, { title: "Application" }, { title: "Rapport" },
                ]} />

                {step === 0 && (
                    <Card>
                        <Alert type="info" showIcon style={{ marginBottom: 16 }}
                            message="Lit la feuille « Règlements ». Pour chaque règlement d'honoraire (colonne Taxe renseignée), remet le montant du décaissement à sa valeur propre (Montant, colonne 7) et enregistre l'honoraire à part (champ taxes), sans l'y additionner. Ne corrige que les montants valant exactement « montant + taxe » ; le SAP et les évaluations ne sont pas touchés. L'aperçu ne modifie rien." />
                        <Dragger accept=".xlsx,.xls" multiple={false}
                            beforeUpload={(f) => { setFichier(f); return false; }}
                            onRemove={() => setFichier(null)} maxCount={1} style={{ marginBottom: 24 }}>
                            <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: "#242166" }} /></p>
                            <p className="ant-upload-text">Cliquez ou déposez le fichier Excel Pool (sinistres)</p>
                            <p className="ant-upload-hint">Le même fichier que la reprise des sinistres GUM/VT</p>
                        </Dragger>
                        {fichier && (
                            <Alert type="success" showIcon style={{ marginBottom: 16 }}
                                message={<Space><FileExcelOutlined style={{ color: "#217346" }} />
                                    <Text strong>{fichier.name}</Text>
                                    <Text type="secondary">({(fichier.size / 1024).toFixed(1)} Ko)</Text></Space>} />
                        )}
                        <div style={{ textAlign: "right" }}>
                            <Button type="primary" size="large" icon={<EyeOutlined />} loading={loading}
                                disabled={!fichier} onClick={previsualiser} style={{ background: "#242166" }}>
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && apercu && (
                    <div>
                        <Alert type="warning" showIcon style={{ marginBottom: 20 }}
                            message="Aperçu — aucune écriture effectuée"
                            description={`${apercu.corriges} décaissement(s) seront corrigés sur ${apercu.sinistresImpactes} sinistre(s). Total honoraires retirés des montants : ${fmt(apercu.montantRetireTotal)}. Vérifiez l'échantillon puis appliquez.`} />
                        <Card style={{ marginBottom: 20 }}><Indics d={apercu} /></Card>
                        {apercu.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des corrections ({apercu.echantillon.length})</Text>}>
                                <Table dataSource={apercu.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        {apercu.lignesErreur?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Space><WarningOutlined style={{ color: "#faad14" }} />
                                    <Text strong>Anomalies ({apercu.lignesErreur.length})</Text></Space>}>
                                <Table dataSource={apercu.lignesErreur.map((m, i) => ({ key: i, message: m }))}
                                    columns={[{ title: "Détail", dataIndex: "message", key: "message",
                                        render: v => <Text type="warning" style={{ fontSize: 12 }}>{v}</Text> }]}
                                    size="small" pagination={{ pageSize: 15 }} scroll={{ y: 220 }} />
                            </Card>
                        )}
                        <Space>
                            <Button onClick={reinitialiser}>Annuler</Button>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(apercu)}>Exporter l'aperçu</Button>
                            <Button type="primary" icon={<ThunderboltOutlined />} disabled={apercu.corriges === 0}
                                onClick={appliquer} style={{ background: "#242166" }}>
                                Appliquer en base ({apercu.corriges})
                            </Button>
                        </Space>
                    </div>
                )}

                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            {loading ? (
                                <>
                                    <LoadingOutlined style={{ fontSize: 48, color: "#242166", marginBottom: 20 }} />
                                    <div style={{ marginBottom: 8 }}><Text strong style={{ fontSize: 16 }}>Correction en cours…</Text></div>
                                    {jobId && (
                                        <div style={{ marginBottom: 12 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Job ID : <Text code>{jobId}</Text></Text>
                                            <Tag icon={<SyncOutlined spin />} color="processing" style={{ marginLeft: 8 }}>
                                                {ETAT_LABELS[jobEtat] || jobEtat}</Tag>
                                        </div>
                                    )}
                                    <Progress percent={progress} strokeColor="#242166" style={{ maxWidth: 500, margin: "0 auto" }} />
                                </>
                            ) : jobEtat === "ERREUR" ? (
                                <>
                                    <CloseCircleFilled style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 20 }} />
                                    <div><Text type="danger" strong style={{ fontSize: 16 }}>L'opération a échoué.</Text></div>
                                    <Button onClick={reinitialiser} style={{ marginTop: 16 }}>Recommencer</Button>
                                </>
                            ) : null}
                        </div>
                    </Card>
                )}

                {step === 3 && rapport && (
                    <div>
                        <Alert type="success" showIcon icon={<CheckCircleFilled style={{ color: "#52c41a" }} />}
                            message={`${rapport.corriges} décaissement(s) corrigés — ${fmt(rapport.montantRetireTotal)} d'honoraires retirés des montants`}
                            description={`Durée : ${(rapport.dureeMs / 1000).toFixed(1)}s — sauvegarde réversible dans m_decaissement_bak_honoraires`} style={{ marginBottom: 20 }} />
                        <Card style={{ marginBottom: 20 }}><Indics d={rapport} /></Card>
                        {rapport.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des corrections ({rapport.echantillon.length})</Text>}>
                                <Table dataSource={rapport.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        <Space>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(rapport)}>Exporter le rapport</Button>
                            <Button type="primary" icon={<RocketOutlined />} onClick={reinitialiser} style={{ background: "#242166" }}>
                                Nouvelle correction
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
